<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-if="section && section.type === 'clause' && innerHtml"
    class="flex hover:bg-highlight-100 rounded-md pr-10 relative"
    :class="isHovered ? 'bg-higlight-100' : ''"
    @mouseenter="() => (isHovered = true)"
    @mouseleave="() => (isHovered = false)"
  >
    <div class="flex py-8 mx-14 mb-4 w-full">
      <section
        :id="`clause-${section.code}`"
        :class="`legalDocumentSection ${isNumberedParagraph ? '' : 'un'}numbered-paragraph span-1 w-full text-justify pl-3`"
        v-html="innerHtml"
      />
    </div>
    <div class="absolute right-0 flex space-y-2 flex-col justify-center mt-3 mr-3" :class="[isHovered ? 'visible' : 'invisible']">
      <EditClauseButton :section="section" size="medium" />
      <RemoveClauseButton v-if="section.optional_but_selected" :section="section" size="medium" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import renderOptions from '@/questionnaire/services/legalDocumentService/renderOptions'

import { compileTemplate } from '@/utils/handlebars'
import EditClauseButton from './EditClauseButton.vue'
import { useLegalDocumentTemplateStore } from '@/questionnaire/stores/legalDocumentTemplate'
import RemoveClauseButton from './RemoveClauseButton.vue'

const props = defineProps<{ code: string; clause: number }>()
const legalDocumentTemplateStore = useLegalDocumentTemplateStore()
const section = legalDocumentTemplateStore.sections[props.code]
const isNumberedParagraph = !!section?.numbered_paragraph

const isHovered = ref(false)

const innerHtml = computed<string>(() => {
  if (html?.value?.documentElement) {
    const sections = html.value.querySelectorAll('body section')

    if (sections?.length) {
      return [...sections]
        .filter(section => section.innerHTML)
        .map(section => section.innerHTML)
        ?.join('')
    }
  }
  return ''
})

const getNestingLevel = (element: Element) => {
  let level = 0
  let parent = element.parentElement

  while (parent) {
    if (parent.tagName === 'OL') {
      level++
    }
    parent = parent.parentElement
  }

  return level
}

const addNumberAttribute = (elemt: Element) => {
  const lists = elemt.querySelectorAll('ol')

  lists.forEach(list => {
    const items = Array.from(list.children).filter((child): child is HTMLLIElement => child.tagName === 'LI')

    items.forEach((item, index) => {
      const level = getNestingLevel(item)

      let number = ''

      if (level === 1) {
        number = isNumberedParagraph ? `${props.clause}.${index + 1}` : `${index + 1}`
      } else {
        number = 'abcdefghijklmnopqrstuvwxyz'.charAt(index)
      }

      const numberSpan = document.createElement('span')
      numberSpan.textContent = number + '.'
      item.insertBefore(numberSpan, item.firstChild)
    })
  })
}

const html = computed<Document>(() => {
  const template = compileTemplate(section.content, renderOptions(section))
  const tempDocument = new DOMParser().parseFromString(template, 'text/html')
  const sectionChildren = tempDocument.querySelector('section')

  console.debug('if has children')
  if (sectionChildren?.children) {
    console.debug('addNumberAttribute')
    addNumberAttribute(sectionChildren)
  }

  return tempDocument
})
</script>

<style>
.legalDocumentSection {
  @apply text-slate-500;

  p {
    @apply text-slate-500 font-normal text-base leading-6;
  }

  u {
    @apply underline decoration-slate-500;
  }

  ul,
  ol {
    @apply list-inside;
    p {
      @apply inline;
    }
  }

  ul {
    @apply pl-6 my-2 list-disc;
  }

  .numbered-paragraph ol > li::before {
    position: absolute;
    content: attr(data-list-item-counter) !important;
  }

  ol {
    @apply pl-6 my-2 list-none;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply font-bold text-black text-lg leading-6 mb-3 !important;
  }
}

#contract-pdf-output-container {
  .numbered-paragraph ul {
    list-style-type: disc;
    list-style-position: outside;
  }

  .numbered-paragraph ol > li {
    padding-left: 1rem;
  }

  .numbered-paragraph > ol > li {
    padding-left: 2rem;
    margin-left: -2rem;

    &::before {
      margin-left: -3rem;
    }
  }
}

#contract-pdf-output-container .numbered-paragraph :is(ol) {
  @apply my-4;
}
</style>
